import { useState } from "react";
import type { FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { adminLogin, useAuth } from "@/hooks/useAuth";

export default function Login() {
  const { isAuthed, ready } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    if (!username.trim() || !password) {
      setError("请填写账号和密码");
      return;
    }
    setLoading(true);
    void adminLogin(username, password).then((res) => {
      setLoading(false);
      if (res.ok) {
        navigate("/");
      } else {
        setError(res.message ?? "登录失败");
      }
    });
  };

  if (!ready) return <div className="min-h-screen bg-background-50" />;
  if (isAuthed) return <Navigate to="/" replace />;

  return (
    <div className="flex min-h-screen w-full bg-background-50">
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-foreground-950 p-12 lg:flex">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-route-line text-xl" />
          </span>
          <span className="font-heading text-base font-semibold text-background-50">域名中转站后台</span>
        </div>

        <div className="relative z-10 max-w-md">
          <h2 className="font-heading text-3xl font-semibold leading-snug text-background-50">
            一个面板，
            <br />
            管好你所有的跳转域名
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-foreground-400">
            批量添加中转域名、设置 301 / 302 / 隐藏跳转，实时掌握每个域名的点击量与运行状态。
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {["批量域名管理", "三种跳转方式", "点击量统计", "一键启停"].map((t) => (
              <span key={t} className="rounded-full bg-foreground-900 px-3 py-1.5 text-xs text-foreground-200">
                {t}
              </span>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 text-xs text-foreground-500">
          <i className="ri-shield-check-line text-accent-400" />
          跳转服务运行稳定
        </div>

        <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-primary-500/10" />
        <div className="pointer-events-none absolute -bottom-20 right-16 h-56 w-56 rounded-full bg-accent-500/10" />
      </div>

      <div className="flex w-full flex-col items-center justify-center px-6 py-12 lg:w-1/2">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-route-line text-xl" />
            </span>
            <span className="font-heading text-base font-semibold text-foreground-950">域名中转站后台</span>
          </div>

          <h1 className="font-heading text-2xl font-semibold text-foreground-950">管理员登录</h1>
          <p className="mt-2 text-sm text-foreground-500">登录后即可管理你的域名跳转规则</p>

          <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-4">
            <label className="flex flex-col gap-2">
              <span className="font-label text-sm font-medium text-foreground-800">账号</span>
              <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 focus-within:border-primary-500">
                <i className="ri-user-3-line text-foreground-400" />
                <input
                  type="text"
                  name="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="请输入管理员账号"
                  className="w-full bg-transparent py-2.5 text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
                />
              </div>
            </label>

            <label className="flex flex-col gap-2">
              <span className="font-label text-sm font-medium text-foreground-800">密码</span>
              <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 focus-within:border-primary-500">
                <i className="ri-lock-2-line text-foreground-400" />
                <input
                  type="password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="请输入密码"
                  className="w-full bg-transparent py-2.5 text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
                />
              </div>
            </label>

            {error ? (
              <div className="flex items-center gap-2 rounded-md bg-secondary-100 px-3 py-2.5 text-sm text-secondary-900">
                <i className="ri-error-warning-line" />
                {error}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={loading}
              className="mt-1 flex items-center justify-center gap-2 rounded-md bg-primary-500 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60 cursor-pointer whitespace-nowrap"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  登录中…
                </>
              ) : (
                <>
                  进入后台
                  <i className="ri-arrow-right-line" />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}